import { useState } from 'react';

import useGoBack from '@/hooks/useGoBack';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import { editArtwork } from '@/shared-components/forms/add-artwork/submit-button/components/EditArtworkHandler.jsx';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';

import SaveDraft from './SaveDraft';

import './styles/ArtworkConfirmationDialog.css';

export default function ArtworkConfirmationDialog({
  open, 
  onClose, // ✅ Ensure this function is correctly passed and used
  children, 
  onConfirm, // ✅ Triggered after saving successfully
  confirmText, 
  formState, 
  setFormState, 
  initialState, 
  previewMode, 
  editMode,
  deleteDraftMode, 
  setFormError, // ✅ Used for error handling in `editArtwork`
  navigate, // ✅ Added for centralized navigation in `editArtwork`
  cacheHandler, // ✅ Added for cache handling in `editArtwork`
  stateHandler,
}) {
  const [saveExitLoading, setSaveExitLoading] = useState(false); // Tracks loading state for "Save and Exit"
  const goBackHandler = useGoBack("/");

  /**
   * ✅ Handles the "Save and Exit" logic
   * - Calls `editArtwork()` to save changes
   * - Closes dialog on success
   * - Handles errors properly
   */
  const onSaveAndExitClick = async () => {
    setSaveExitLoading(true); // Start the spinner

    const success = await editArtwork({
      formState, 
      setFormError, 
      navigate, 
      cacheHandler, 
      goBackHandler,
      onArtworkEdited: () => {
        stateHandler.set("temporalPopup", {
          text: "Artwork edited successfully!",
          type: "navbar-present",
        });
      },
    });

    setSaveExitLoading(false); // Stop the spinner

    if (success) {
      // ✅ Call onConfirm() to handle any exit logic
      if (typeof onConfirm === 'function') {
        onConfirm();
      }
  
      // ✅ Ensure onClose() is properly called after saving
      if (typeof onClose === 'function') {
        onClose();
      }
    } else {      
      // ✅ Close the popup even if there's an error
      if (typeof onClose === 'function') {
        onClose();
      }
    }
  };

  return (
    <>
      <GenericPopup className="artwork-confirmation-dialog remove-scrollbar" open={open} opacity>
        {/* ✅ Close Icon */}
        <XIcon className="artwork-confirmation-dialog-popup__close" onClick={() => {
          if (typeof onClose === "function") {
            onClose(); // Call the close function
          } else {
            console.warn("⚠️ onClose is not defined.");
          }
        }} 
        />

        {children}

        <div className="artwork-confirmation-dialog__action-buttons element-non-selectable">
          {/* Conditionally render "Save and Exit" button based on editMode */}
          {editMode && (
            <>
              <ContinueButton loading={saveExitLoading}
                onClick={onSaveAndExitClick}
                className={'artwork-confirmation-dialog__save-exit-button'} >
                Save and exit
              </ContinueButton>
            </>
          )}
                    
          {/* ✅ "Yes, Exit" Button (Triggers both onConfirm and onClose) */}
          <ContinueButton 
            onClick={() => {
              if (typeof onConfirm === 'function') {
                onConfirm();
              }
              if (typeof onClose === 'function') {
                onClose();
              } else {
                console.warn("❌ onClose is not defined.");
              }
            }}
            className={`artwork-confirmation-dialog__yes-exit-button`}
            medium
          >
            {confirmText}
          </ContinueButton>

          {/* ✅ Save Draft Button (Only shown in non-edit/non-preview mode) */}
          {(!previewMode && !editMode && !deleteDraftMode) && 
            <SaveDraft 
              formState={formState} 
              setFormState={setFormState} 
              initialState={initialState}
              buttonFormat={true}
              onClose={goBackHandler}
            />
          }
        </div>
      </GenericPopup>
    </>
  );
}